import type React from 'react';
import { ActionIcon, Box, Tooltip } from '@mantine/core';
import type { WorkspaceTabletPane, WorkspaceViewportMode } from '../hooks/useAdaptiveWorkspaceLayout';

type AdaptiveWorkspaceShellProps = {
  viewportMode: WorkspaceViewportMode;
  tabletPane: WorkspaceTabletPane;
  desktopListCollapsed: boolean;
  effectiveListCollapsed: boolean;
  isMobileStageVisible: boolean;
  navRail: React.ReactNode;
  listPanel: React.ReactNode;
  stage: React.ReactNode;
  plainStage?: boolean;
  mobileNav: React.ReactNode;
  onToggleDesktopListCollapsed: () => void;
};

export function AdaptiveWorkspaceShell({
  viewportMode,
  tabletPane,
  desktopListCollapsed,
  effectiveListCollapsed,
  isMobileStageVisible,
  navRail,
  listPanel,
  stage,
  plainStage = false,
  mobileNav,
  onToggleDesktopListCollapsed,
}: AdaptiveWorkspaceShellProps) {
  const sidebarToggleLabel = desktopListCollapsed ? '展开侧栏' : '收起侧栏';

  return (
    <Box
      data-testid="workspace-adaptive-shell"
      data-viewport-mode={viewportMode}
      data-tablet-pane={tabletPane}
      data-desktop-list-collapsed={desktopListCollapsed ? 'true' : 'false'}
      data-effective-list-collapsed={effectiveListCollapsed ? 'true' : 'false'}
      className="workspace-adaptive-shell"
    >
      <Box data-testid="workspace-nav-region" className="workspace-adaptive-shell__rail">
        {navRail}
      </Box>

      <Box
        data-testid="workspace-list-region"
        className="workspace-adaptive-shell__list"
        data-mobile-visible={!isMobileStageVisible ? 'true' : 'false'}
        data-tablet-visible={tabletPane === 'list' ? 'true' : 'false'}
        data-desktop-collapsed={effectiveListCollapsed ? 'true' : 'false'}
      >
        {listPanel}
      </Box>

      <Box
        data-testid="workspace-stage-region"
        className="workspace-adaptive-shell__stage"
        data-mobile-visible={isMobileStageVisible ? 'true' : 'false'}
        data-tablet-visible={tabletPane === 'stage' ? 'true' : 'false'}
      >
        <Box className="workspace-stage-container" data-plain={plainStage ? 'true' : 'false'}>
          {stage}
        </Box>
      </Box>

      <Tooltip
        label={sidebarToggleLabel}
        position="left"
        withArrow
        openDelay={250}
      >
        <ActionIcon
          data-testid="workspace-list-collapse-toggle"
          className="workspace-list-collapse-toggle"
          aria-label={sidebarToggleLabel}
          onClick={onToggleDesktopListCollapsed}
          radius="xl"
          size={52}
          variant="default"
          color="terracotta"
        >
          <svg
            className="workspace-list-collapse-toggle__arrow"
            data-direction={desktopListCollapsed ? 'right' : 'left'}
            aria-hidden="true"
            viewBox="0 0 12 12"
            focusable="false"
          >
            <path d={desktopListCollapsed ? 'M4.75 3.25L7.5 6L4.75 8.75' : 'M7.25 3.25L4.5 6L7.25 8.75'} />
          </svg>
        </ActionIcon>
      </Tooltip>

      {!isMobileStageVisible && mobileNav}
    </Box>
  );
}
